<script setup lang="ts">
/**
 * The footer: a riveted steel band (the kit's `.k-panel`, full width) carrying
 * three columns of even height: the wordmark with the copyright line and the
 * social knobs, the company with its contact address, and the game's documents
 * as a 2x2 grid of rows; payment methods run underneath. Each block shows only
 * when the CMS has it (`/api/site-info`,
 * `/api/cms/social-links`, `/api/legal`; a down or empty CMS resolves to
 * nothing, never an error). Documents and contact are `.k-well` rows with a
 * painted icon, social links are `.k-knob`s. The two documents every game
 * ships stand in when the list is empty. Opening a document is the page's
 * business (`legal`).
 */
import { computed, onMounted, ref } from 'vue';

import { consent } from '@/consent/consent';
import { useI18n } from '@/i18n';
import { fetchLegalList, fetchSiteInfo, fetchSocialLinks, type CompanyInfo, type PaymentMethodInfo, type SocialLink } from '@/net/site-api';
import type { LegalDoc } from '@/state/legal-slugs';
import GameIcon from '@/ui/kit/GameIcon.vue';

import { iconFor } from '../social-icons';
import LandingWordmark from './LandingWordmark.vue';

defineEmits<{ legal: [slug: string] }>();
const { t } = useI18n();

const company = ref<CompanyInfo | null>(null);
const social = ref<SocialLink[]>([]);
const paymentMethods = ref<PaymentMethodInfo[]>([]);
const legalDocs = ref<LegalDoc[]>([]);

const year = new Date().getFullYear();

const legalButtons = computed<LegalDoc[]>(() =>
  legalDocs.value.length
    ? legalDocs.value
    : [
        { slug: 'terms-of-service', title: t('landing.enlist.terms') },
        { slug: 'privacy-policy', title: t('landing.enlist.privacy') },
      ],
);

onMounted(async () => {
  const [info, links, docs] = await Promise.all([fetchSiteInfo(), fetchSocialLinks(), fetchLegalList()]);
  company.value = info.company;
  paymentMethods.value = info.paymentMethods;
  social.value = links;
  legalDocs.value = docs;
});
</script>

<template>
  <footer class="foot">
    <div class="band k-panel">
      <div class="grid">
        <div class="col brand">
          <LandingWordmark :size="52" />
          <p class="copy">© {{ year }} {{ company?.name || t('landing.enlist.credit') }}</p>
          <div v-if="social.length" class="social" :aria-label="t('landing.footer.follow')">
            <a
              v-for="s in social"
              :key="s.url"
              :href="s.url"
              target="_blank"
              rel="noopener noreferrer"
              class="k-knob"
              :aria-label="s.platform"
              data-test="footer-social"
            >
              <img v-if="iconFor(s.platform)" :src="iconFor(s.platform)!" alt="" width="22" height="22" />
              <span v-else class="k-label">{{ s.platform }}</span>
            </a>
          </div>
        </div>

        <div v-if="company" class="col">
          <h3 class="k-label head">{{ t('landing.footer.company') }}</h3>
          <div class="lines">
            <p class="k-label line">{{ company.name }}</p>
            <p v-if="company.address" class="line">{{ company.address }}</p>
            <p v-if="company.registration" class="line">{{ company.registration }}</p>
          </div>
          <template v-if="company.email">
            <h3 class="k-label head">{{ t('landing.footer.contact') }}</h3>
            <a class="row k-well" :href="`mailto:${company.email}`" :title="company.email">
              <GameIcon name="letter" :size="30" />
              <span class="k-label mail">{{ company.email }}</span>
            </a>
          </template>
        </div>

        <div class="col docs">
          <h3 class="k-label head">{{ t('landing.footer.game') }}</h3>
          <div class="doc-grid">
            <button v-for="doc in legalButtons" :key="doc.slug" type="button" class="row k-well" @click="$emit('legal', doc.slug)">
              <GameIcon name="orders" :size="30" />
              <span class="k-label">{{ doc.title }}</span>
            </button>
            <button type="button" class="row k-well" data-test="footer-cookie-settings" @click="consent.openSettings()">
              <GameIcon name="cog" :size="30" />
              <span class="k-label">{{ t('consent.settingsLink') }}</span>
            </button>
          </div>
        </div>

        <div v-if="paymentMethods.length" class="col wide">
          <h3 class="k-label head">{{ t('landing.footer.payment') }}</h3>
          <div class="payments k-well">
            <img v-for="m in paymentMethods" :key="m.slug" :src="`/payment-methods/${m.icon}.svg`" :alt="m.name" loading="lazy" width="32" height="20" />
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.foot {
  background: #0a1119;
}

/* The band is the kit's steel panel run edge to edge; padding clears its painted rim. */
.band {
  padding: clamp(28px, 4vw, 44px) max(22px, env(safe-area-inset-left)) calc(clamp(28px, 4vw, 44px) + env(safe-area-inset-bottom)) max(22px, env(safe-area-inset-right));
}

.grid {
  display: grid;
  gap: 24px 32px;
  width: min(1180px, 100%);
  margin: 0 auto;
}

.col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.brand {
  gap: 12px;
}

.copy {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.85rem;
  font-weight: 600;
  color: #b9c7d4;
}

.head {
  margin: 0;
  font-size: 0.85rem;
  text-transform: uppercase;
  color: var(--kit-gold);
}

.lines + .head {
  margin-top: 8px;
}

/* A document or contact row: a dark groove with its painted icon, 44 px tall at least. */
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 48px;
  padding: 6px 12px 6px 8px;
  border: 0;
  color: #fff;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  touch-action: manipulation;
}

.row .k-label {
  min-width: 0;
  font-size: 0.95rem;
}

/* An address breaks only at its own punctuation, never mid-word; too long for the row, it is cut with the full text in the title. */
.mail {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.doc-grid {
  display: grid;
  gap: 8px;
}

.lines {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.line {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.9rem;
  color: #dbe6ef;
}

.lines .k-label.line {
  color: #fff;
}

.social,
.payments {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* Room for the knobs' rim and shadow. */
.social {
  gap: 14px;
  padding: 2px 8px 8px 2px;
}

.payments {
  padding: 10px 12px;
  gap: 10px;
}

.payments img {
  height: 22px;
  width: auto;
  padding: 3px 6px;
  border-radius: 6px;
  background: #f4efe2;
}

@media (hover: hover) {
  .row:hover {
    filter: brightness(1.25);
  }
}

@media (min-width: 560px) {
  .doc-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 720px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .docs,
  .wide {
    grid-column: 1 / -1;
  }
}

/* Desktop: brand, company, documents side by side; the documents take the double column their 2x2 grid needs. */
@media (min-width: 1100px) {
  .grid {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.9fr);
  }

  .docs {
    grid-column: auto;
  }
}
</style>
